import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { 
    Card, 
    CardContent, 
    CardHeader, 
    Divider, 
    FormControlLabel, 
    FormGroup, 
    Grid, 
    Switch,
    Typography,
    Tooltip
} from "@material-ui/core";
import InfoIcon from '@material-ui/icons/Info';

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(2),
    },
    container: {
        gap: "0.5rem",
    },
    switch: {
        width: "max-content",
        margin: 0,
    },
    consentRow: {
        display: "flex",
        alignItems: "center",
        columnGap: "1rem"
    }
}));
const Consent = props => {

    const { className, useMetaDataSettings, consentSettingsContent, ...rest } = props;
    const {
        isLoading,
        handleChange,
        CONSENT_PROMOTIONAL,
        CONSENT_TRANSACTIONAL,
        ENABLE_UTM_TRACKING,
        settingsMetaData,
        isSettingsMetaDataUpdating
    } = useMetaDataSettings;

    const classes = useStyles();
    const [promotionalCampaings, setPromotionalCampaings] = useState(false);
    const [transactionalCampaigns, setTransactionalCampaigns] = useState(false);
    const [utmTracking, setUtmTracking] = useState(false);

    const SkeletonCardContent = () => {
        return (
            <CardContent>
                <Grid item md={6} xs={12}>
                    <Typography display="block" style={{ fontSize: '2.2rem' }}>
                    <Skeleton />
                    </Typography>
                </Grid>
            </CardContent>
        );
    };

    useEffect(() => {
        if (settingsMetaData) {
            setPromotionalCampaings(settingsMetaData[CONSENT_PROMOTIONAL]);
            setTransactionalCampaigns(settingsMetaData[CONSENT_TRANSACTIONAL]);
            setUtmTracking(settingsMetaData[ENABLE_UTM_TRACKING]);
        }
    }, [settingsMetaData]);
    
    const handleConsentChange = (event, key) => {
        let name, value;
        if (key === 'promotional') {
            name = CONSENT_PROMOTIONAL;
            value = !promotionalCampaings;
            setPromotionalCampaings(!promotionalCampaings);
        } else if (key === 'transactional') {
            name = CONSENT_TRANSACTIONAL;
            value = !transactionalCampaigns;
            setTransactionalCampaigns(!transactionalCampaigns);
        } else if (key === 'utm') {
            name = ENABLE_UTM_TRACKING;
            value = !utmTracking;
            setUtmTracking(!utmTracking);
        }
        handleChange({ target: { name: name, value: value } })
    }

    return (
        <Card {...rest} className={clsx(classes.root, className)}>
            <form>
                <CardHeader
                    subheader={consentSettingsContent?.description || `Manage campaign settings`}
                    title={consentSettingsContent?.title || `Campaign`}
                />
                <Divider />
                {isLoading || isSettingsMetaDataUpdating ? (
                    <SkeletonCardContent /> 
                ) : (
                    <CardContent>
                        <Grid>
                            <FormGroup className={classes.container}>
                                <Grid className={classes.consentRow}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={promotionalCampaings}
                                                onChange={e => handleConsentChange(e, 'promotional')}
                                                name="promotionalCampaings"
                                            />
                                        }
                                        label={consentSettingsContent?.promotionalToggleLabel || `Promotional Campaign Consent`}
                                        className={classes.switch}
                                    />
                                    <Tooltip
                                        className={classes.infoIconForSchedule}
                                        title={consentSettingsContent?.promotionalToggleTooltip || `Enable consent for promotional campaigns as derived from the incoming reservation or event data.`}>
                                        <InfoIcon />
                                    </Tooltip>
                                </Grid>
                                <Grid className={classes.consentRow}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={transactionalCampaigns}
                                                onChange={e => handleConsentChange(e, 'transactional')}
                                                name="transactionalCampaings"
                                            />
                                        }
                                        label={consentSettingsContent?.transactionalToggleLabel || `Transactional Campaign Consent`}
                                        className={classes.switch}
                                    />
                                    <Tooltip
                                        className={classes.infoIconForSchedule}
                                        title={consentSettingsContent?.transactionalToggleTooltip || `Enable consent for transactional campaigns as derived from the incoming reservation or event data.`}>
                                        <InfoIcon />
                                    </Tooltip>
                                </Grid>
                                <Grid className={classes.consentRow}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={utmTracking}
                                                onChange={e => handleConsentChange(e, 'utm')}
                                                name="utmTracking"
                                            />
                                        }
                                        label={consentSettingsContent?.utmToggleLabel || `UTM Tracking`}
                                        className={classes.switch}
                                    />
                                    <Tooltip
                                        className={classes.infoIconForSchedule}
                                        title={consentSettingsContent?.utmToggleTooltip || `Enable UTM tracking for campaigns.`}>
                                        <InfoIcon />
                                    </Tooltip>
                                </Grid>
                            </FormGroup>
                        </Grid>
                    </CardContent>
                )}
            </form>
        </Card>
    );
};

Consent.propTypes = {
    className: PropTypes.string,
    useMetaDataSettings: PropTypes.object
};

export default Consent;